import { useInfiniteQuery } from 'react-query';
import { baseApiUrl, fetcher, ServerErrorResponse, createParamString } from 'data_sources/constants';
import { DTBPackageHistoryResponse } from 'views/App/Portal/DTB/_types/dtb';

export async function getDTBDistributionPackageHistory(
  orgId: string,
  dtbPackageId: string,
  nextToken?: string,
  signal?: AbortSignal
): Promise<DTBPackageHistoryResponse> {
  const baseUrl = `${baseApiUrl()}/lexiverse/dtbs/v1/organizations/${orgId}/packages/${dtbPackageId}/distribution-history`;
  const params = createParamString({
    nextToken: nextToken || '',
  });

  const response = await fetcher({
    method: 'GET',
    url: params ? `${baseUrl}?${params}` : baseUrl,
    options: { signal },
  });

  return response as DTBPackageHistoryResponse;
}

export default function useDTBDistributionPackageHistory(
  orgId: string,
  dtbPackageId: string,
  onError?: (errors: ServerErrorResponse) => void
) {
  if (!orgId || !dtbPackageId) {
    return {
      data: undefined,
      isFetching: false,
      isError: false,
      hasNextPage: false,
      fetchNextPage: () => Promise.resolve(),
      refetch: () => Promise.resolve(),
    } as any;
  }

  const historyQuery = useInfiniteQuery<DTBPackageHistoryResponse, ServerErrorResponse>(
    ['dtbPackageHistory', orgId, dtbPackageId],
    async ({ pageParam = '', signal }) => {
      try {
        const data = await getDTBDistributionPackageHistory(orgId, dtbPackageId, pageParam, signal);
        return data;
      } catch (error: unknown) {
        if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
          onError(error as ServerErrorResponse);
        }
        throw error;
      }
    },
    {
      keepPreviousData: true,
      getNextPageParam: (lastPage) => lastPage.nextToken || undefined,
    }
  );

  return {
    ...historyQuery,
    data: historyQuery.data,
  };
}
